import { Text as MantineText, Button, Flex, NumberInput, TextInput, Badge } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { Timestamp } from "firebase/firestore";
import { ChangeEvent, useCallback, useState, useMemo, useEffect } from "react";
import usePendingNotasStore from "../../stores/usePendingNotasStore";

interface Fase {
  id: string;
  titulo: string;
  cor: string;
  checked: boolean;
}

interface MyState {
  nota: string;
  justificativa: string;
}

const EditaNotaItem = ({ item, fase }: { item: TMedia; fase: Fase }) => {
  const originalValues = { nota: item.nota, justificativa: item.justificativa };
  const addPendingNota = usePendingNotasStore((state) => state.addPendingNota);
  const pendingNotas = usePendingNotasStore((state) => state.pendingNotas);

  // Verifica se há uma nota pendente para este item (usa id ou cria chave alternativa)
  const itemKey = item.id || `${item.idResposta}-${item.fase}`;
  const pendingNota = useMemo(() => {
    return pendingNotas.get(itemKey);
  }, [pendingNotas, itemKey]);

  // Se há nota pendente, usa os valores pendentes; caso contrário, usa os valores originais
  const initialValues =
    pendingNota && pendingNota.type === "edicao"
      ? { nota: pendingNota.nota, justificativa: pendingNota.justificativa }
      : { nota: item.nota, justificativa: item.justificativa };

  const [values, setValues] = useState<MyState>(initialValues);
  const hasChanges = values.nota !== originalValues.nota || values.justificativa !== originalValues.justificativa;
  const isPending = pendingNota !== undefined;

  // Sincroniza estado quando pendingNota mudar
  useEffect(() => {
    if (pendingNota && pendingNota.type === "edicao") {
      setValues({ nota: pendingNota.nota, justificativa: pendingNota.justificativa });
    }
  }, [pendingNota]);

  const handleUpdate = useCallback(() => {
    // Adiciona ao estado pendente ao invés de salvar imediatamente
    addPendingNota(itemKey, {
      type: "edicao",
      mediaId: itemKey,
      mediaOriginal: item,
      nota: values.nota,
      justificativa: values.justificativa
    });

    showNotification({
      message: `Nota marcada para salvar. Clique em "Fechar Avaliação e Atualizar Notas" para salvar todas.`,
      color: "blue",
      autoClose: 3000
    });
  }, [addPendingNota, itemKey, item, values]);
  return (
    <Flex gap="md" direction="row" justify="left" align="center">
      <MantineText>{fase && fase?.titulo}</MantineText>
      <NumberInput style={{ width: 80 }} placeholder="Nota" onChange={onChangeNumber(setValues)} value={values.nota} />
      <TextInput placeholder="Justificativa da nota" onChange={onChangeText(setValues)} value={values.justificativa} />
      <Flex gap="md" direction="row" justify="space-between" align="center">
        <Button onClick={handleUpdate} disabled={values.nota === "" || !values.justificativa || !hasChanges}>
          {isPending ? "Atualizar Pendente" : "Marcar para Salvar"}
        </Button>
        {isPending && (
          <Badge color="yellow" variant="light">
            Pendente
          </Badge>
        )}
        {!isPending && item.updatedAt && (
          <MantineText size="sm" c="dimmed">
            Atualizado em:{" "}
            {item.updatedAt instanceof Timestamp ? item.updatedAt.toDate().toLocaleString() : item.updatedAt.toString()}
          </MantineText>
        )}
        {item.desconsiderado && (
          <MantineText size="sm" c="dimmed">
            *Essa nota foi desconsiderada*
          </MantineText>
        )}
      </Flex>
    </Flex>
  );
};

export default EditaNotaItem;

const onChangeNumber = (setValues: React.Dispatch<React.SetStateAction<MyState>>) => {
  return (value: string) => {
    setValues((values) => ({ ...values, nota: value }));
  };
};

const onChangeText = (setValues: React.Dispatch<React.SetStateAction<MyState>>) => {
  return (event: ChangeEvent<HTMLInputElement>) => {
    setValues((values) => ({ ...values, justificativa: event.target.value }));
  };
};
